import "./Structure.css";

import { useMemo } from "react";

import { RoundedPlusSignIcon, TrashDark } from "../../../../../../assets";
import { InvestmentDetailsInterface, SelectData } from "../../../../../../models";
import { Button, Column, FullPageCard, Row } from "../../../../../../widget";
import { NextPreviousPageButtons } from "../../../components";

type StructureProps = {
  investments: InvestmentDetailsInterface[];
  selectedInvestmentId: string | undefined;
  instrumentSelectData: SelectData;
  associationSelectData: SelectData;

  onSelectInvestment: (id: string) => void;
  onAddInvestment: () => void;
  onRemoveInvestment: (id: string) => void;
};

export const Structure = ({
  investments,
  selectedInvestmentId,
  instrumentSelectData,
  associationSelectData,
  onSelectInvestment,
  onAddInvestment,
  onRemoveInvestment,
}: StructureProps): JSX.Element => {
  const hasInvestments = investments.length > 0;

  const instrumentsLookup = useMemo(
    () => Object.fromEntries(instrumentSelectData.map((x) => [x.key, x.value])),
    [instrumentSelectData]
  );
  const associationsLookup = useMemo(
    () => Object.fromEntries(associationSelectData.map((x) => [x.key, x.value])),
    [associationSelectData]
  );

  return (
    <div className="StructureCard">
      <FullPageCard corners="rounded" padding="ll" paddingBottom="s">
        <div className="StructureContainer">
          <Row spacingV="ml">
            <Column span={12}>
              <h2>Deal structure</h2>
            </Column>
          </Row>
          {!hasInvestments ? (
            <Row spacingV="l">
              <Column span={12}>
                <p className="body1 StructureEmptyState">No investments yet</p>
              </Column>
            </Row>
          ) : (
            <>
              <Row spacingV="l">
                <Column span={12}>
                  <p className="body1 StructureIntro">
                    Add investments to define how capital is deployed within this deal, and associate them to your
                    chosen organisations, projects, and/or landholdings.
                  </p>
                </Column>
              </Row>
              <Row spacingV="l">
                <Column span={12}>
                  <Button
                    text="Add investment"
                    icon={<RoundedPlusSignIcon />}
                    iconPosition="left"
                    variant="reversedPrimary"
                    hasNoPadding
                    minWidth={false}
                    className="StructureAddButton"
                    onClick={onAddInvestment}
                  />
                </Column>
              </Row>
              <Row spacingV="xl">
                <Column span={12}>
                  <div className="StructureInvestmentList">
                    {investments.map((investment) => (
                      <div
                        key={investment.key}
                        className={`StructureInvestmentCard ${
                          investment.key === selectedInvestmentId ? "StructureInvestmentCardSelected" : ""
                        }`}
                        onClick={() => onSelectInvestment(investment.key)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            onSelectInvestment(investment.key);
                          }
                        }}
                      >
                        <div>
                          <p className="body1">
                            <b>
                              {investment.instrument !== undefined
                                ? instrumentsLookup[investment.instrument]
                                : undefined}
                            </b>
                          </p>
                          <p className="body3">
                            {investment.associations.length > 0
                              ? investment.associations.map((x) => associationsLookup[x]).join(", ")
                              : undefined}
                          </p>
                        </div>
                        <Button
                          text=""
                          variant="secondary"
                          icon={<TrashDark className="StructureTrashIcon" />}
                          className="TrashButton"
                          hasNoPadding
                          minWidth={false}
                          onClick={(event) => {
                            event.stopPropagation();
                            onRemoveInvestment(investment.key);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </Column>
              </Row>
            </>
          )}
          <Row className="StructureFooter">
            <Column span={12}>
              <NextPreviousPageButtons />
            </Column>
          </Row>
        </div>
      </FullPageCard>
    </div>
  );
};
